import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Link,
  Alert,
  Stack,
  InputAdornment,
} from "@mui/material";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Login from "@mui/icons-material/Login";

export default function GuestEmailVerificationForm({
  email,
  setEmail,
  checking,
  emailExists,
  errorMsg,
  onVerifyEmail,
  onNavigateLogin,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: "#ffffff",
        boxShadow: "0 10px 28px rgba(15,23,42,0.06)",
        maxWidth: 720,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          background:
            "linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(59,130,246,0.12) 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmailOutlined sx={{ color: "#0f172a" }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
            Verify your primary email
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          We’ll check if you already have an account associated with this email.
        </Typography>
      </Box>
      <Stack spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
        <TextField
          label="Primary Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter" && !checking) onVerifyEmail();
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
          We’ll never share your email. Used only to identify or create your account.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            onClick={onVerifyEmail}
            disabled={checking}
            endIcon={
              checking ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />
            }
            sx={{
              px: 3,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "0 6px 16px rgba(34,197,94,0.25)",
            }}
          >
            {checking ? "Verifying..." : "Continue"}
          </Button>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            We’ll check if your email already exists in our system.
          </Typography>
        </Box>
        {errorMsg && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMsg}
          </Alert>
        )}
        {emailExists && (
          <Alert
            severity="info"
            icon={<Login fontSize="small" />}
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={onNavigateLogin}>
                Go to Login
              </Button>
            }
          >
            We found an account for this email. Please sign in to continue. If you don’t remember
            your password, use “Forgot Password” on the sign‑in page. Need help? Contact{" "}
            <Link href="mailto:support@blueoceansp.atlassian.net" underline="always">
              support@blueoceansp.atlassian.net
            </Link>
            .
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

GuestEmailVerificationForm.propTypes = {
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  checking: PropTypes.bool.isRequired,
  emailExists: PropTypes.bool,
  errorMsg: PropTypes.string,
  onVerifyEmail: PropTypes.func.isRequired,
  onNavigateLogin: PropTypes.func.isRequired,
};
